import type { Scenario } from '../types';

export type ScenarioAssetLayer = 'draft' | 'master';

export interface ResolveScenarioAssetUrlOptions {
  origin?: string;
  layer?: ScenarioAssetLayer;
  /** Spaces origin or CDN hostname, e.g. https://bucket.fra1.cdn.digitaloceanspaces.com */
  cdnBaseUrl?: string;
  /** Object-key prefix in the bucket (default: scenarios). */
  spacesPrefix?: string;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function stripWrappingSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, '');
}

/** Public Spaces / CDN base URL from Vite env (unset in local disk mode). */
export function getConfiguredSpacesCdnBaseUrl(): string | undefined {
  const raw = import.meta.env.VITE_SPACES_CDN_BASE_URL?.trim();
  return raw ? stripTrailingSlash(raw) : undefined;
}

export function getConfiguredSpacesPrefix(): string {
  const raw = import.meta.env.VITE_SPACES_PREFIX?.trim();
  return stripWrappingSlashes(raw || 'scenarios') || 'scenarios';
}

/** Cache-bust key derived from scenario metadata (bump version when assets change). */
export function getScenarioAssetCacheKey(scenario: Pick<Scenario, 'metadata'>): string {
  const version = scenario.metadata.version?.trim();
  return version || '0';
}

export function stripAssetCacheQuery(url: string): string {
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

export function withScenarioAssetCacheBust(
  url: string | undefined,
  cacheKey: string,
): string | undefined {
  if (!url || !cacheKey) return url;
  const base = stripAssetCacheQuery(url);
  return `${base}?v=${encodeURIComponent(cacheKey)}`;
}

export function resolveScenarioAssetUrl(
  path: string | undefined,
  scenarioId: string,
  cacheKey: string,
  options?: ResolveScenarioAssetUrlOptions,
): string | undefined {
  if (!path) return path;

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return withScenarioAssetCacheBust(path, cacheKey);
  }

  const origin = options?.origin ?? '';
  if (path.startsWith('/')) {
    return withScenarioAssetCacheBust(path, cacheKey);
  }

  const layer = options?.layer ?? 'master';
  const cdnBaseUrl = options?.cdnBaseUrl ?? getConfiguredSpacesCdnBaseUrl();

  if (layer !== 'draft' && cdnBaseUrl) {
    const spacesPrefix = stripWrappingSlashes(
      options?.spacesPrefix ?? getConfiguredSpacesPrefix(),
    );
    const absolute = `${cdnBaseUrl}/${spacesPrefix}/${scenarioId}/master/${path}`;
    return withScenarioAssetCacheBust(absolute, cacheKey);
  }

  const layerPrefix = layer === 'draft' ? 'draft/' : '';
  const absolute = `${origin}/scenarios/${scenarioId}/${layerPrefix}${path}`;

  return withScenarioAssetCacheBust(absolute, cacheKey);
}

/** Bump semver patch (e.g. 0.2.0 → 0.2.1) so asset URLs invalidate browser cache. */
export function bumpScenarioAssetVersion(version: string | undefined): string {
  const raw = (version ?? '0.1.0').trim() || '0.1.0';
  const m = raw.match(/^(\d+)\.(\d+)\.(\d+)(.*)$/);
  if (m) {
    return `${m[1]}.${m[2]}.${Number(m[3]) + 1}${m[4]}`;
  }
  return `${raw}-r${Date.now()}`;
}
